/**
 * Typed API client using openapi-fetch.
 *
 * See frontend/src/api/client.ts for full documentation.
 */
import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { getDistributorToken, redirectToManage } from "../auth/useDistributorAuth";
import { getDashboardApiClient } from "./dashboardClient";
import { apiUrl, liveApiUrl } from "./config";

export { apiUrl, liveApiUrl };

/**
 * Public client for unauthenticated requests.
 */
export const publicClient = createClient<paths>({
  baseUrl: apiUrl,
});

/**
 * Auth middleware that reads the SSO token from localStorage.
 */
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = getDistributorToken();
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) {
      if (import.meta.env.DEV) {
        const { clearDistributorAuth } = await import("../auth/useDistributorAuth");
        clearDistributorAuth();
      } else {
        redirectToManage();
      }
    }
    return response;
  },
};

/**
 * Authenticated client singleton.
 */
const _authClient = createClient<paths>({
  baseUrl: apiUrl,
});
_authClient.use(authMiddleware);

/**
 * Get the authenticated client.
 *
 * Path-aware: returns the dashboard client (internal auth) when on /dashboard/* routes,
 * otherwise returns the distributor client.
 */
export function getApiClient() {
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/dashboard")) {
    return getDashboardApiClient();
  }
  return _authClient;
}

/**
 * @deprecated No longer needed — client is initialized at module level.
 */
export function initApiClient() {
  return _authClient;
}
